// FILE: src/components/app/bases/BudgetBaseComponent/hooks/useBudgets.js

import * as React from "react";
import { subscribeUserBudgets } from "../../../../../utils/firebase/features/budgets/fetchers";
import {
  createBudgetItem,
  updateBudgetItem,
  deleteBudgetItem,
} from "../../../../../utils/firebase/features/budgets/writers";
import { getUserTransactionsCached } from "../../../../../utils/firebase/cache/transactionsCached";
import { AccountsAPI, TransactionsAPI } from "../../../../../utils/firebase";

/* ----------------------------- helpers ----------------------------- */

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function addDays(date, n) {
  const t = new Date(date);
  t.setDate(t.getDate() + n);
  return t;
}

function daysUntil(dateStr) {
  if (!dateStr) return 0;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr); due.setHours(0, 0, 0, 0);
  return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
}

function num(n) {
  const v = typeof n === "number" ? n : parseFloat(n);
  return Number.isFinite(v) ? v : 0;
}

function pickAccountBalance(a) {
  return num(a?.accountBalance ?? a?.balance ?? a?.currentBalance);
}

function normalizeTxArray(maybe) {
  if (Array.isArray(maybe)) return maybe;
  if (maybe && Array.isArray(maybe.rows)) return maybe.rows;
  if (maybe && Array.isArray(maybe.items)) return maybe.items;
  return [];
}

function groupByBudgetId(rows) {
  const out = new Map();
  for (const r of rows) {
    const bid = r?.budgetId;
    if (!bid) continue;
    if (!out.has(bid)) out.set(bid, []);
    out.get(bid).push(r);
  }
  return out;
}

/* feasibility bands */
function feasibilityLabel({ amount, bufferPct, targetDate }, { netWorth, monthlyFCF }) {
  const days = Math.max(0, daysUntil(targetDate));
  const months = days / 30;
  const bufferMax = amount * (1 + (bufferPct || 0) / 100);
  const available = netWorth + months * (monthlyFCF || 0);
  if (available >= bufferMax) return { label: "Likely doable", level: "success" };
  if (available >= amount) return { label: "Tight (buffer at risk)", level: "warning" };
  return { label: "Unlikely", level: "danger" };
}

/* ------------------------------ hook ------------------------------- */

export default function useBudgets({ user }) {
  const userEmail = String(user?.email || "").toLowerCase();

  const [budgets, setBudgets] = React.useState([]);              // raw budget docs
  const [accounts, setAccounts] = React.useState([]);            // raw accounts
  const [netWorth, setNetWorth] = React.useState(0);
  const [monthlyFreeCashFlow, setMonthlyFreeCashFlow] = React.useState(0);
  const [plannedByBudget, setPlannedByBudget] = React.useState(new Map()); // Map<budgetId, tx[]>

  /* ---- subscribe budgets ---- */
  React.useEffect(() => {
    if (!userEmail) return;
    const unsub = subscribeUserBudgets({
      userEmail,
      cap: 1000,
      onData: (items) => setBudgets(Array.isArray(items) ? items : []),
      onError: () => setBudgets([]),
    });
    return () => unsub?.();
  }, [userEmail]);

  /* ---- subscribe accounts & compute net worth ---- */
  React.useEffect(() => {
    if (!userEmail) return;
    const unsub = AccountsAPI.subscribeUserAccounts({
      userEmail,
      cap: 1000,
      onData: (items) => {
        const arr = Array.isArray(items) ? items : [];
        setAccounts(arr);
        const sum = arr.reduce((s, a) => s + pickAccountBalance(a), 0);
        setNetWorth(sum);
      },
      onError: () => {
        setAccounts([]);
        setNetWorth(0);
      },
    });
    return () => unsub?.();
  }, [userEmail]);

  /* ---- compute monthly free cash flow (last ~90 days) ---- */
  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!userEmail) { setMonthlyFreeCashFlow(0); return; }
      try {
        const to = new Date();
        const from = addDays(to, -90);
        const res = await getUserTransactionsCached({
          userEmail,
          fromDate: toISO(from),
          toDate: toISO(to),
          cap: 5000,
          latestFirst: false,
        });
        const rows = normalizeTxArray(res);

        let incomeAbs = 0, expenseAbs = 0;
        for (const r of rows) {
          const t = String(r?.typeName || "").toLowerCase();
          const amtAbs = num(r?.amountAbs ?? Math.abs(num(r?.amount)));
          if (t === "income") incomeAbs += amtAbs;
          else if (t === "expense") expenseAbs += amtAbs;
          // ignore transfers for FCF
        }
        const days = Math.max(1, Math.round((to - from) / (1000 * 60 * 60 * 24)));
        const months = Math.max(1, days / 30);
        const mfcf = (incomeAbs - expenseAbs) / months;
        if (alive) setMonthlyFreeCashFlow(mfcf);
      } catch {
        if (alive) setMonthlyFreeCashFlow(0);
      }
    })();
    return () => { alive = false; };
  }, [userEmail]);

  /* ---- planned transactions (future window; group by budgetId) ---- */
  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!userEmail) { setPlannedByBudget(new Map()); return; }
      try {
        const today = new Date();
        const from = toISO(today);
        const to = toISO(addDays(today, 365));

        const res = await getUserTransactionsCached({
          userEmail,
          fromDate: from,
          toDate: to,
          cap: 5000,
          latestFirst: false,
        });
        const rows = normalizeTxArray(res);

        // filter to future-dated only (treat planned == future date)
        const todayMid = new Date(from);
        const futureRows = rows.filter((r) => {
          const d = new Date(r?.date);
          return Number.isFinite(d.getTime()) && d >= todayMid;
        });

        const grouped = groupByBudgetId(futureRows);
        if (alive) setPlannedByBudget(grouped);
      } catch {
        if (alive) setPlannedByBudget(new Map());
      }
    })();
    return () => { alive = false; };
  }, [userEmail]);

  /* ---- enriched budgets ---- */
  const itemsEnriched = React.useMemo(() => {
    const ctx = { netWorth, monthlyFCF: monthlyFreeCashFlow };
    return (budgets || []).map((b) => {
      const planned = plannedByBudget.get(b.id) || [];
      const allocated = planned.reduce((s, t) => s + num(t?.amountAbs ?? Math.abs(num(t?.amount))), 0);
      const remaining = Math.max(0, num(b.amount) - allocated);
      const bufferMax = num(b.amount) * (1 + (num(b.bufferPct) || 0) / 100);
      const days = daysUntil(b.targetDate);
      const feas = feasibilityLabel(
        { amount: num(b.amount), bufferPct: num(b.bufferPct), targetDate: b.targetDate },
        ctx
      );
      return {
        ...b,
        _computed: {
          allocated,
          remaining,
          bufferMax,
          days,
          feasibility: feas, // {label, level}
          plannedCount: planned.length,
        },
      };
    })
      // sort: upcoming first (by target date asc), then newest created first
      .sort((a, b) => {
        const ad = Date.parse(a?.targetDate) || 0;
        const bd = Date.parse(b?.targetDate) || 0;
        if (ad !== bd) return ad - bd;
        const ac = a?.createdAt?.toMillis ? a.createdAt.toMillis() : Date.parse(a?.createdAt) || 0;
        const bc = b?.createdAt?.toMillis ? b.createdAt.toMillis() : Date.parse(b?.createdAt) || 0;
        return bc - ac;
      });
  }, [budgets, plannedByBudget, netWorth, monthlyFreeCashFlow]);

  /* --------------------------- actions ----------------------------- */

  const createBudget = React.useCallback(async (item) => {
    if (!user?.uid) throw new Error("Not signed in.");
    const rec = await createBudgetItem({ user, item });
    return rec?.id;
  }, [user]);

  const updateBudget = React.useCallback(async (item) => {
    if (!user?.uid) throw new Error("Not signed in.");
    return updateBudgetItem({ user, item });
  }, [user]);

  const deleteBudget = React.useCallback(async (id) => {
    if (!user?.uid) throw new Error("Not signed in.");
    await deleteBudgetItem({ user, id });
  }, [user]);

  const addPlannedTx = React.useCallback(async (budget, txPartial) => {
    if (!user?.uid) throw new Error("Not signed in.");
    if (!budget?.id) throw new Error("Missing budget id.");

    const desc = String(txPartial?.description || "").trim();
    const date = String(txPartial?.date || "").trim();
    const amt = num(txPartial?.amount);

    if (!desc || !date || !Number.isFinite(amt) || amt <= 0) {
      throw new Error("Invalid planned transaction payload.");
    }

    // normalize type to UI labels
    const tnRaw = String(txPartial?.typeName || "").toLowerCase();
    const typeName = tnRaw.includes("income") ? "Income" : "Expense";
    const amount = typeName === "Income" ? Math.abs(amt) : -Math.abs(amt);

    // merge tag paths: user-provided + stable budget tag
    const userTagPath = Array.isArray(txPartial?.tagPath) ? txPartial.tagPath.filter(Boolean) : [];
    const budgetTagPath = Array.isArray(budget?.budgetTagPath) ? budget.budgetTagPath : [];
    const tagPath = [...userTagPath, ...budgetTagPath];

    // default currency from budget's account (if specified)
    let currencyCode = txPartial?.currencyCode;
    if (!currencyCode && budget?.source?.type === "account" && budget?.source?.accountId) {
      const acct = accounts.find((a) => String(a?.id) === String(budget.source.accountId));
      if (acct?.currencyCode) currencyCode = String(acct.currencyCode);
    }

    const accountId = budget?.source?.type === "account" ? budget?.source?.accountId : undefined;

    await TransactionsAPI.createManualTransaction({
      user,
      tx: {
        typeName,
        description: desc,
        date,
        amount,
        currencyCode,
        accountId,
        tagPath,
        budgetId: budget.id, // some backends may drop this; we also match via tag in the selector
      },
    });
  }, [user, accounts]);


  const deletePlannedTx = React.useCallback(async (txId) => {
    if (!user?.uid) throw new Error("Not signed in.");
    if (!txId) return;
    await TransactionsAPI.deleteTransactions({ user, ids: [String(txId)] });
  }, [user]);

  return {
    items: budgets,
    itemsEnriched,
    accounts,
    netWorth,
    monthlyFreeCashFlow,
    createBudget,
    updateBudget,
    deleteBudget,
    addPlannedTx,
    deletePlannedTx,
  };
}
